// CarryIt legal document copy and versions.
// Each document has a "version". Bump the version when you materially change
// the copy — users will be re-prompted to accept ToS + Privacy on next login.
// Grey wording rules baked in throughout:
//   - CarryIt = intermediary + technology platform (never a courier/logistics company)
//   - Carriers = independent individuals (never employees/agents/partners)
//   - Protection ≠ insurance (no "insurance", "premium", "policy", "claim")

// Sentinel used for values that still need to be filled in. Once every value
// below is real, this sentinel should not appear anywhere in the running app.
export const PLACEHOLDER = "[TO BE FILLED]";

export const BUSINESS = {
  legalName: "Manveet Global Ventures Private Limited",
  brandName: "CarryIt",
  website: "carryit.co.in",
  address: "C/O Manveet Kaur, Birarhwal Road, Near Bus Stand, Birarhwal, District Patiala, Punjab – 147201, India",
  cin: "U47733PB2026PTC067664",
  gstin: "03AAUCM6914C1ZH",
  email: "support@carryit.co.in",
  phone: "+91 78141 43477",
  jurisdiction: "Patiala, Punjab",
};

export const GRIEVANCE_OFFICER = {
  name: "Manveet Kaur",
  designation: "Grievance Officer",
  email: "grievance@carryit.co.in",
  phone: "+91 78141 43477",
  address: "C/O Manveet Kaur, Birarhwal Road, Near Bus Stand, Birarhwal, District Patiala, Punjab – 147201, India",
};

export const DOC_VERSIONS = {
  terms: "1.0",
  privacy: "1.0",
  refund: "1.0",
  prohibited: "1.0",
  protection: "1.0",
  carrier_agreement: "1.0",
  grievance: "1.0",
};

export const EFFECTIVE_DATE = "1 February 2026";

// ─── TERMS OF SERVICE ────────────────────────────────────────────────────
export const TERMS = {
  slug: "terms",
  title: "Terms of Service",
  version: DOC_VERSIONS.terms,
  effective: EFFECTIVE_DATE,
  sections: [
    {
      heading: "1. About CarryIt",
      body: [
        `CarryIt is an intermediary and technology platform (the "Platform") operated by ${BUSINESS.legalName} ("CarryIt", "we", "our", "us"). The Platform connects individuals who wish to send a parcel ("Senders") with independent travellers who are already making a journey and are willing to carry a parcel along their existing trip ("Carriers"). CarryIt is not a courier company, transporter, logistics provider or delivery service.`,
        `The contract for the transport of any parcel is a direct contract between the Sender and the Carrier. CarryIt only facilitates the connection between them, provides the technology, and collects the Sender's payment through its payment partner. The Carrier is paid only after delivery is confirmed. CarryIt is not a party to that transport contract.`,
      ],
    },
    {
      heading: "2. Eligibility",
      body: [
        "To use the Platform you must be at least 18 years of age, be legally competent to enter into a binding contract under Indian law, and complete the identity verification (KYC) process on the Platform. You must provide accurate information about yourself and keep your account details up to date.",
        "You may only hold one account. You must not use the Platform on behalf of any other person unless expressly authorised in writing by CarryIt.",
      ],
    },
    {
      heading: "3. How the service works",
      body: [
        "A Sender creates a parcel listing on the Platform describing the pickup and drop-off locations, the size and contents of the parcel, and the declared value. The Platform displays independent Carriers who are travelling on a matching route, along with an all-inclusive price for each Carrier.",
        "The Sender chooses a Carrier and pays through the Platform. Payments are collected by our payment partner, Razorpay, and settled to CarryIt. The Carrier collects the parcel from the Sender using a pickup PIN. On delivery, the recipient confirms receipt with a drop-off PIN. Once the drop-off PIN is confirmed, CarryIt pays the Carrier their earnings. The platform commission is retained by CarryIt.",
      ],
    },
    {
      heading: "4. Carriers are independent",
      body: [
        "Carriers are independent individuals who provide transport services in their personal capacity. They are not employees, agents, staff, partners or representatives of CarryIt. CarryIt does not direct, supervise, schedule or control any Carrier, their route, mode of transport or timing.",
        "Every Carrier declares, for each trip they publish, that they were already making that journey and are carrying the parcel along an existing route. CarryIt relies on that declaration.",
      ],
    },
    {
      heading: "5. Pricing, commission and payment",
      body: [
        "The price shown to the Sender is inclusive of the Carrier's earnings and CarryIt's platform commission. The Platform commission is retained by CarryIt for the technology, matching, payment handling and support services it provides.",
        "The Sender's payment is collected by our payment partner, Razorpay, and settled to CarryIt. The Carrier is paid only after the recipient confirms delivery with the drop-off PIN.",
      ],
    },
    {
      heading: "6. Prohibited items and inspection",
      body: [
        "Certain items must not be sent through the Platform. The full list is set out in the Prohibited Items Policy. Before accepting a parcel, a Carrier has the right to inspect its contents. If the Carrier finds anything they are not willing or able to carry, they may refuse the parcel without penalty.",
        "Once a parcel is sealed and accepted by a Carrier, the Carrier must not open the parcel during transit. Tampering with a sealed parcel is a serious breach of these Terms and may result in account termination.",
      ],
    },
    {
      heading: "7. Trust tiers and declared value limits",
      body: [
        "Each verified user has a trust tier (New, Tier 1, Tier 2, Tier 3). Higher tiers unlock higher declared value limits and additional permissions (such as carrying perishables). Trust tiers are earned through completed deliveries, ratings and dispute-free behaviour.",
        "A parcel's declared value must not exceed the maximum permitted for the Carrier's trust tier. If the declared value is above the tier limit, the Carrier will not appear in matching for that parcel.",
      ],
    },
    {
      heading: "8. Off-platform arrangements are prohibited",
      body: [
        "Users must not contact each other to arrange deliveries, payments or handovers outside the Platform. All communication between Senders and Carriers must remain on the in-app chat until delivery is complete. Any attempt to circumvent the Platform will result in immediate suspension.",
      ],
    },
    {
      heading: "9. CarryIt Protection and limitation of liability",
      body: [
        "The Platform offers CarryIt Protection — a platform-funded compensation guarantee. It is not insurance. Full terms are in the CarryIt Protection page.",
        `CarryIt's total liability to any user, for any claim arising out of or in connection with the Platform, is strictly limited to the CarryIt Protection amount for the Carrier's trust tier on the relevant delivery. CarryIt is not liable for any indirect, consequential, special, incidental or exemplary damages, including loss of profit, data or goodwill.`,
      ],
    },
    {
      heading: "10. Account suspension and termination",
      body: [
        "We may suspend or terminate any account, without prior notice, if we reasonably believe the user has breached these Terms, engaged in fraud, misrepresented their identity, attempted to carry a prohibited item, arranged an off-platform deal, or otherwise behaved in a way that puts the community at risk.",
        "You may close your account at any time from the app. Closure does not affect deliveries that are already in progress — those must be completed first.",
      ],
    },
    {
      heading: "11. Governing law and jurisdiction",
      body: [
        `These Terms are governed by the laws of India and constitute a binding agreement between you and ${BUSINESS.legalName}. Subject to any statutory rights you may have as a consumer, the courts at ${BUSINESS.jurisdiction} have exclusive jurisdiction over any dispute arising out of or in connection with these Terms or the Platform.`,
      ],
    },
    {
      heading: "12. Changes to these Terms",
      body: [
        "We may update these Terms from time to time. If we make a material change, we will notify you in the app and require you to accept the new version before you continue to use the Platform.",
      ],
    },
    {
      heading: "13. Contact",
      body: [
        `Questions about these Terms? Write to us at ${BUSINESS.email} or by post to ${BUSINESS.legalName}, ${BUSINESS.address}.`,
      ],
    },
  ],
};

// ─── PRIVACY POLICY ──────────────────────────────────────────────────────
export const PRIVACY = {
  slug: "privacy",
  title: "Privacy Policy",
  version: DOC_VERSIONS.privacy,
  effective: EFFECTIVE_DATE,
  sections: [
    {
      heading: "1. Who this applies to",
      body: [
        `This Privacy Policy explains how ${BUSINESS.legalName} ("CarryIt", "we") collects, uses, discloses and protects your personal information when you use the CarryIt Platform.`,
      ],
    },
    {
      heading: "2. Information we collect",
      body: [
        "We collect the following categories of personal information:",
        "• Identity information: name, date of birth, photograph.",
        "• Contact information: phone number, email address, postal address.",
        "• KYC documents: Aadhaar-based verification result, PAN, driving licence, vehicle registration certificate. Aadhaar numbers are stored only in a masked form (last four digits) — we never store the full Aadhaar number.",
        "• Payment information: UPI ID, bank account details (for Carrier payouts).",
        "• Trip and parcel information: pickup and drop-off addresses, contents description, declared value, parcel photographs.",
        "• Location data: precise device location when the app is open for pickup and delivery navigation. You control this permission at the OS level.",
        "• Device and usage information: device type, operating system, IP address, app version, activity logs.",
      ],
    },
    {
      heading: "3. Why we collect it",
      body: [
        "We use your information to: verify your identity and eligibility; match Senders with Carriers on overlapping routes; calculate accurate pricing; process payments and Carrier payouts; enable in-app chat and delivery tracking; investigate fraud, disputes and safety incidents; comply with our obligations under Indian law (including tax, anti-money-laundering and IT rules); and improve the Platform.",
      ],
    },
    {
      heading: "4. KYC through third-party providers",
      body: [
        "Identity verification is carried out through third-party KYC providers that are permitted under Indian law to perform Aadhaar-based, PAN and driving-licence checks. Those providers receive only the information they need to complete the verification. The result of the verification is stored with your CarryIt account.",
      ],
    },
    {
      heading: "5. Information we share",
      body: [
        "Once a Sender chooses a Carrier and the parcel is accepted, we share the following limited information between them: first name, in-app rating, pickup and drop-off addresses, and the parcel description. Phone numbers are never shown to the other party. Senders and Carriers contact each other through in-app chat and, where available, a masked in-app call that does not reveal either number. Full street addresses are only unmasked when they are needed for the pickup or drop-off.",
        "We also share information with our payment providers (to process transactions), KYC providers (for identity verification), cloud infrastructure providers (to host the Platform), and government authorities where required by law.",
        "We do not sell your personal information to any third party for advertising purposes.",
      ],
    },
    {
      heading: "6. Retention",
      body: [
        "We retain your account information for as long as your account is active. Transaction records (deliveries, payments, invoices) are retained for the minimum period required under the Income Tax Act, GST law and other applicable Indian financial regulations — currently at least eight years from the end of the relevant financial year.",
        "KYC records are retained for the period required under the Prevention of Money-Laundering rules. Location traces are retained for 90 days after the relevant delivery is completed.",
      ],
    },
    {
      heading: "7. Your rights",
      body: [
        "You have the right to access the personal information we hold about you, to have inaccurate information corrected, and to request deletion of your account and associated personal data (subject to the retention obligations described above).",
        `To exercise any of these rights, write to our Grievance Officer at the address on the /grievance page or email ${BUSINESS.email}. We will respond within the timelines set out in that page.`,
      ],
    },
    {
      heading: "8. Security",
      body: [
        "We protect personal information using technical and organisational safeguards including encryption in transit (HTTPS/TLS), encryption at rest for sensitive fields, access controls, audit logging, and regular security reviews. No system is completely secure — please notify us immediately at the Grievance Officer email if you suspect your account has been compromised.",
      ],
    },
    {
      heading: "9. Cookies and similar technologies",
      body: [
        "The CarryIt website uses cookies for session management, remembering your preferences, and understanding how the site is used. You can control cookies through your browser settings. Blocking essential cookies may prevent parts of the site from working.",
      ],
    },
    {
      heading: "10. Grievance Officer",
      body: [
        "Complaints about privacy or personal information should be addressed to our Grievance Officer. See the /grievance page for name, contact details and response timelines.",
      ],
    },
    {
      heading: "11. Changes to this Policy",
      body: [
        "If we materially change this Policy, we will notify you in the app and require you to accept the updated version before continuing to use the Platform.",
      ],
    },
  ],
};

// ─── REFUND & CANCELLATION ──────────────────────────────────────────────
export const REFUND = {
  slug: "refund-policy",
  title: "Refund and Cancellation Policy",
  version: DOC_VERSIONS.refund,
  effective: EFFECTIVE_DATE,
  sections: [
    {
      heading: "1. When the Carrier is paid",
      body: [
        "Payments made by a Sender are collected by our payment partner, Razorpay, and settled to CarryIt. The Carrier is paid only after the recipient confirms delivery with the drop-off PIN. This means refunds are straightforward until pickup and PIN-based after that.",
      ],
    },
    {
      heading: "2. Cancellation by the Sender — before a Carrier accepts",
      body: [
        "If you cancel a parcel before any Carrier has accepted it, you receive a 100% refund. There is no cancellation fee.",
      ],
    },
    {
      heading: "3. Cancellation by the Sender — after a Carrier accepts, before pickup",
      body: [
        `If you cancel after a Carrier has accepted but before pickup, a cancellation fee of ₹50 is deducted to compensate the Carrier for the reserved slot. This fee is waived if you cancel within 15 minutes of the Carrier's acceptance. The balance is refunded to your CarryIt balance or original payment method.`,
        "If the cancellation is because the Carrier delayed pickup beyond the agreed window, no cancellation fee is charged and you receive a 100% refund.",
      ],
    },
    {
      heading: "4. Cancellation by the Carrier after accepting",
      body: [
        "If a Carrier cancels after accepting your parcel, you receive a 100% refund. No fee is deducted. The affected Carrier's trust tier and rating are impacted.",
      ],
    },
    {
      heading: "5. Failed or undelivered parcel",
      body: [
        "If a parcel is picked up by a Carrier but not delivered within the reasonable window agreed for the trip, contact support from the delivery screen. We will investigate. If we confirm the parcel is undelivered:",
        "• You receive a 100% refund of your payment.",
        "• You may also claim CarryIt Protection compensation for the loss of the parcel, subject to the terms on the /protection page.",
      ],
    },
    {
      heading: "6. Damaged parcel",
      body: [
        "If the parcel is delivered but the recipient reports damage at the time of drop-off PIN confirmation (with photographs), you may claim CarryIt Protection compensation. Full details of the process and evidence required are on the /protection page.",
      ],
    },
    {
      heading: "7. Refund timelines and method",
      body: [
        `Refunds to your CarryIt balance are instant. Refunds to your original payment method (UPI, card or bank) are processed within 5 to 7 business days from the date of confirmation. Actual credit to your bank depends on your bank's processing time.`,
      ],
    },
    {
      heading: "8. How to request a refund",
      body: [
        `Open the relevant delivery in the app, tap "Get help", and choose the appropriate reason. For anything the app does not cover, email ${BUSINESS.email} within 7 days of the incident.`,
      ],
    },
  ],
};

// ─── PROHIBITED ITEMS ────────────────────────────────────────────────────
export const PROHIBITED = {
  slug: "prohibited-items",
  title: "Prohibited Items Policy",
  version: DOC_VERSIONS.prohibited,
  effective: EFFECTIVE_DATE,
  sections: [
    {
      heading: "1. Items you must never send on CarryIt",
      body: [
        "The following items are strictly prohibited on the CarryIt Platform. Attempting to send any of them is a serious breach of our Terms of Service and may result in account termination and referral to law-enforcement authorities.",
        "• Cash and currency (Indian or foreign)",
        "• Gold, silver, jewellery and precious stones with a declared value above the Carrier's trust tier limit",
        "• Illegal drugs and narcotic substances",
        "• Weapons, ammunition, explosives, fireworks and pyrotechnics",
        "• Flammable, corrosive, radioactive or otherwise hazardous materials",
        "• Live animals, birds and reptiles",
        "• Human remains, body parts, blood or biological samples",
        "• Counterfeit goods and items that infringe intellectual property",
        "• Tobacco products where restricted by law and alcoholic beverages where restricted by state law",
        "• Prescription medicines without a valid prescription from the sender",
        "• Perishable food unless the Carrier has expressly opted in to carrying perishables",
        "• Liquids of any kind on bike (two-wheeler) Carriers",
        "• Any item the Sender is not legally entitled to send, own or possess",
        "• Anything otherwise prohibited under any applicable law in India",
      ],
    },
    {
      heading: "2. Your responsibilities as a Sender",
      body: [
        "You must accurately declare the contents and the value of every parcel you send. False, misleading or incomplete declaration is a breach of our Terms. False declaration voids CarryIt Protection for that parcel and can result in termination of your account.",
        "You are legally responsible for the contents of your parcel. CarryIt is not liable for any legal, regulatory or civil consequence that arises from what you send.",
      ],
    },
    {
      heading: "3. Carrier's right to inspect and refuse",
      body: [
        "Before accepting a parcel, the Carrier has the right to inspect the contents. If the Carrier is not satisfied that the parcel is safe, legal, and matches the declared contents, the Carrier may refuse without any penalty.",
        "Once a parcel is sealed and accepted, the Carrier must not open it during transit. Tampering is a serious breach of the Carrier Agreement.",
      ],
    },
    {
      heading: "4. Consequences of a prohibited item being found",
      body: [
        "If a prohibited item is discovered in your parcel at any stage:",
        "• The Carrier may immediately return the parcel to the Sender and cancel the delivery.",
        "• CarryIt Protection does not apply to the parcel.",
        "• CarryIt may suspend or terminate your account and, where required, report the matter to law-enforcement authorities.",
      ],
    },
  ],
};

// ─── PROTECTION ──────────────────────────────────────────────────────────
export const PROTECTION = {
  slug: "protection",
  title: "CarryIt Protection",
  version: DOC_VERSIONS.protection,
  effective: EFFECTIVE_DATE,
  sections: [
    {
      heading: "Important — Protection is not insurance",
      body: [
        `CarryIt Protection is a voluntary, platform-funded compensation guarantee provided by ${BUSINESS.brandName}. It is not an insurance product and is not underwritten by an insurance company. Words like "insurance", "insured", "premium", "policy" and "claim" do not apply. We use the terms "protection", "coverage", "compensation" and "protection fee".`,
      ],
    },
    {
      heading: "1. Protection fee",
      body: [
        `When CarryIt Protection is added to a parcel, a protection fee is charged as a small percentage of the declared value of the parcel, subject to a minimum protection fee of ₹50. The protection fee is shown transparently as part of the total price before you confirm the booking.`,
      ],
    },
    {
      heading: "2. Maximum compensation",
      body: [
        "The maximum compensation payable under CarryIt Protection is limited to the declared value of the parcel, and further capped by the Carrier's trust tier limit for that delivery. The compensation is not additional to a refund — where both are payable, the refund is deducted from the compensation.",
      ],
    },
    {
      heading: "3. What is covered",
      body: [
        "• Loss of the parcel during transit (parcel picked up by the Carrier but never delivered).",
        "• Physical damage to the parcel during transit, reported by the recipient at the moment of drop-off PIN confirmation with clear photographs.",
      ],
    },
    {
      heading: "4. What is not covered",
      body: [
        "• Any prohibited item listed in the Prohibited Items Policy.",
        "• Contents that were falsely declared, misleading, or undervalued.",
        "• Damage caused by inadequate packaging by the Sender.",
        "• Damage that pre-existed the pickup, or wear-and-tear.",
        "• Delays in delivery (Protection covers loss and damage, not lateness).",
        "• Any indirect, consequential or emotional loss.",
        "• Any item excluded by applicable Indian law.",
      ],
    },
    {
      heading: "5. How to request compensation",
      body: [
        "Open the delivery in the app and tap \"Report an issue\" within 24 hours of the drop-off PIN event (or, for a lost parcel, within 24 hours of the agreed delivery window ending). Attach clear photographs, the recipient's statement, and any supporting documents (invoices, screenshots).",
        "You must not attempt to move, repair or discard the damaged parcel until the issue is closed by CarryIt.",
      ],
    },
    {
      heading: "6. Our review process",
      body: [
        `CarryIt will acknowledge every compensation request within 48 hours. We aim to close the review within 7 business days from the date all requested evidence has been received. During the review we may ask both Sender and Carrier for additional information.`,
        "If compensation is approved, it is credited to the Sender's CarryIt balance by default (or to the original payment method on request). If the request is rejected, we will explain the reason in writing and you may escalate through the Grievance Redressal process.",
      ],
    },
    {
      heading: "7. Interaction with the Carrier",
      body: [
        "CarryIt Protection is paid by CarryIt out of its own funds. It does not affect the Carrier's payout when the fault is not attributable to the Carrier. Where our investigation shows a Carrier acted negligently or breached the Carrier Agreement, we may recover the compensation from the Carrier under that Agreement.",
      ],
    },
  ],
};

// ─── CARRIER AGREEMENT ───────────────────────────────────────────────────
export const CARRIER = {
  slug: "carrier-agreement",
  title: "Carrier Agreement",
  version: DOC_VERSIONS.carrier_agreement,
  effective: EFFECTIVE_DATE,
  sections: [
    {
      heading: "1. Independent status",
      body: [
        `You provide transport services on the CarryIt Platform in your personal capacity as an independent individual. You are not an employee, agent, staff member, partner or representative of ${BUSINESS.legalName}. Nothing in this Agreement creates an employment, agency, partnership or joint-venture relationship between you and CarryIt.`,
        "CarryIt does not direct, supervise or control your travel, your route, your mode of transport, your schedule or your day-to-day work. You choose whether to publish a trip, which parcels to accept, and how you make the journey.",
      ],
    },
    {
      heading: "2. Existing-trip declaration",
      body: [
        "For every trip you publish, you declare that you were already making that journey for your own reasons and that you are only carrying a parcel along an existing, pre-planned route. You will not create or extend a journey solely for the purpose of carrying a parcel.",
      ],
    },
    {
      heading: "3. Your responsibilities",
      body: [
        "You are responsible for your own income tax, GST (where applicable), professional licences, vehicle registration and insurance, and any other regulatory compliance associated with your travel and the earnings you receive on the Platform.",
        "You must keep your KYC documents current on the Platform. You must inform us promptly of any change to your driving licence, vehicle registration or contact details.",
      ],
    },
    {
      heading: "4. Handling a parcel",
      body: [
        "Before accepting a parcel, you have the right to inspect its contents and refuse it if it is not safe, legal, or does not match the declared contents.",
        "Once a parcel is sealed and accepted by you, you must not open it during transit. Tampering with a sealed parcel is a fundamental breach of this Agreement and will lead to termination of your account.",
      ],
    },
    {
      heading: "5. Prohibited items",
      body: [
        "You must not carry any item listed in the Prohibited Items Policy. If, on inspection, you discover a prohibited item, you must refuse the parcel and report it in the app.",
      ],
    },
    {
      heading: "6. Liability for negligence",
      body: [
        "You are responsible for loss of or damage to a parcel that is caused by your negligence, wilful misconduct, or breach of this Agreement. CarryIt may recover any compensation paid to a Sender under CarryIt Protection from your future earnings, in accordance with the process set out on the /protection page.",
      ],
    },
    {
      heading: "7. Conduct",
      body: [
        "You will behave professionally and respectfully with every Sender and every recipient. You will not share the personal details of any Sender, recipient or other user with any third party. You will not solicit off-platform arrangements — all communication and payment must remain on the Platform.",
      ],
    },
    {
      heading: "8. Payment",
      body: [
        `Your earnings for each delivery are recorded in your CarryIt Earnings as soon as the recipient confirms delivery with the drop-off PIN. Earnings are a record of what CarryIt owes you; they cannot be topped up, transferred or spent in the app. You may request a withdrawal to your own verified bank account or UPI ID at any time, after any COD commission owed is deducted. Standard bank processing timelines apply. Payouts are typically credited to your bank within 2 to 3 business days.`,
      ],
    },
    {
      heading: "9. Suspension and termination",
      body: [
        "CarryIt may suspend or terminate your Carrier account, without prior notice, if you breach this Agreement, misrepresent your identity, attempt to carry a prohibited item, tamper with a sealed parcel, act unsafely or dishonestly, receive repeated poor ratings, or arrange off-platform deliveries.",
        "You may stop accepting parcels at any time. Any parcel already accepted must be delivered before you close your account.",
      ],
    },
    {
      heading: "10. Governing law",
      body: [
        `This Agreement is governed by the laws of India and is entered into between you and ${BUSINESS.legalName}. Subject to any statutory rights you may have, the courts at ${BUSINESS.jurisdiction} have exclusive jurisdiction over any dispute arising out of it.`,
      ],
    },
  ],
};

// ─── GRIEVANCE ───────────────────────────────────────────────────────────
export const GRIEVANCE = {
  slug: "grievance",
  title: "Grievance Redressal",
  version: DOC_VERSIONS.grievance,
  effective: EFFECTIVE_DATE,
};

// ─── CONTACT (not a versioned legal document) ───────────────────────────
export const CONTACT_META = {
  slug: "contact",
  title: "Contact us",
};

// Handy list of everything, in footer order.
export const DELIVERY = {
  slug: "delivery-policy",
  title: "Delivery Policy",
  version: "1.0",
  effective: EFFECTIVE_DATE,
  sections: [
    { heading: "1. What CarryIt is", body: [
      "CarryIt is a marketplace that connects Senders with verified travellers (Carriers) who are already making a journey. CarryIt is not a courier company and does not itself transport parcels. Each delivery is carried out by an independent Carrier travelling on their own trip.",
    ]},
    { heading: "2. How a delivery works", body: [
      "• Request: the Sender posts a parcel and requests up to four travellers going that way, or accepts an offer from a traveller.",
      "• Acceptance: the first traveller to accept is matched. Payment is taken only after acceptance.",
      "• Pickup: the Sender hands the parcel over and shares a one-time PIN (OTP) with the Carrier, who enters it in the app to confirm the handover.",
      "• Transit: the parcel travels with the Carrier on their journey. Status updates appear in the app.",
      "• Delivery: the receiver shares a second one-time PIN (OTP) with the Carrier at drop-off, which confirms the delivery.",
    ]},
    { heading: "3. Timelines", body: [
      "Delivery timing depends on the Carrier's own journey. Any estimated pickup or delivery time shown in the app is indicative only and is not guaranteed.",
    ]},
    { heading: "4. Prohibited items", body: [
      "The following must never be sent through CarryIt:",
      "• Illegal substances, narcotics or drugs",
      "• Weapons, ammunition or replicas",
      "• Explosives, flammable or otherwise hazardous materials",
      "• Cash, gold and jewellery",
      "• Live animals",
      "• Perishables",
      "• Medicines that require a prescription",
      "• Anything restricted or prohibited under Indian law",
      "Senders must declare the contents of every parcel truthfully and are solely responsible for what they hand over. See also the Prohibited Items page.",
    ]},
    { heading: "5. Right to refuse", body: [
      "A Carrier may refuse to accept or carry any parcel, including at the point of pickup if it does not match its description. CarryIt may refuse, hold or return any parcel at its discretion.",
    ]},
    { heading: "6. Delays and failed delivery", body: [
      "• If a Carrier cancels before pickup, the Sender is notified and can request other travellers. Any payment taken is refunded as set out in the Refund and Cancellation Policy.",
      "• If a Carrier is delayed, the Sender and Carrier can coordinate through in-app chat. Estimated times may change.",
      "• If the receiver is unavailable at drop-off, the Carrier will contact the Sender through the app to arrange a new time or an alternative handover. Unresolved cases can be raised with CarryIt support.",
    ]},
    { heading: "7. Tracking and contact", body: [
      "Delivery status is shown in the app at every step. Senders and Carriers can message each other through in-app chat once a parcel is accepted. Phone numbers are never shared between the parties.",
    ]},
    { heading: "8. Cancellations and refunds", body: [
      "Cancellations and refunds are covered by our Refund and Cancellation Policy at /refund-policy.",
    ]},
    { heading: "9. Liability and compensation", body: [
      "[TO BE COMPLETED — PENDING LEGAL REVIEW]",
    ]},
    { heading: "10. Contact for delivery issues", body: [
      `For any delivery issue, write to ${BUSINESS.email} or call ${BUSINESS.phone}. Post: ${BUSINESS.legalName}, ${BUSINESS.address}.`,
    ]},
  ],
};

export const ALL_DOCS = [
  { slug: "terms", title: "Terms of Service" },
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "refund-policy", title: "Refund & Cancellation" },
  { slug: "delivery-policy", title: "Delivery Policy" },
  { slug: "prohibited-items", title: "Prohibited Items" },
  { slug: "protection", title: "CarryIt Protection" },
  { slug: "carrier-agreement", title: "Carrier Agreement" },
  { slug: "grievance", title: "Grievance Redressal" },
  { slug: "contact", title: "Contact" },
];
